import { odooApi } from '../index';
import { Talent, PaginatedResponse } from '@/types';
import { getOdooLang } from '@/config/api.config';
import { 
  transformOdooTalentToTalent,
  transformOdooPaginatedResponse,
  extractErrorMessage 
} from '../transforms';

/**
 * Talents API Endpoints (Connected to Odoo Backend)
 * 
 * This file contains all talent-related API endpoints.
 * Now connected to real Odoo API at /api/moys/talents
 */

export const talentsApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get talents from Odoo
    getTalents: builder.query<
      PaginatedResponse<Talent>,
      { page?: number; pageSize?: number; category?: string; lang?: 'ar' | 'en' | 'ku' }
    >({
      query: ({ page = 1, pageSize = 10, category, lang = 'ar' }) => {
        const odooLang = getOdooLang(lang);
        
        // Build query string with pagination (backend expects page and page_size via _get_pagination_params)
        const params = new URLSearchParams({
          lang: odooLang,
          page: String(page),
          page_size: String(pageSize),
        });
        // Note: Backend doesn't currently support category filter, but we keep it for future use
        if (category) params.append('category', category);
        
        return {
          url: `/api/moys/talents?${params.toString()}`,
          method: 'POST',
          // No body - backend reads from query parameters
        };
      },
      transformResponse: (response: any, _meta, arg) => {
        try {
          return transformOdooPaginatedResponse(
            response,
            transformOdooTalentToTalent,
            arg.page || 1,
            arg.pageSize || 10,
            arg.lang || 'ar'
          );
        } catch (error) {
          console.error('[Talents API] Transform error:', error);
          throw error;
        }
      },
      transformErrorResponse: (response) => {
        return extractErrorMessage(response);
      },
      providesTags: (result) =>
        result && Array.isArray(result.data)
          ? [
              ...result.data.map(({ id }) => ({ type: 'Talent' as const, id })),
              { type: 'Talent', id: 'LIST' },
            ]
          : [{ type: 'Talent', id: 'LIST' }],
    }),

    // Get single talent by ID
    getTalent: builder.query<Talent, { id: string; lang?: 'ar' | 'en' | 'ku' }>({
      async queryFn({ id, lang = 'ar' }, _api, _extraOptions, baseQuery) {
        const odooLang = getOdooLang(lang);
        
        try {
          // Parse ID to integer (backend expects integer)
          const talentId = parseInt(id, 10);
          if (isNaN(talentId)) {
            console.error('[Talents API] Invalid talent ID:', id);
            return { error: { status: 400, data: 'Invalid talent ID' } as any };
          }
          
          // Use the ID-based endpoint
          const result = await baseQuery({
            url: `/api/moys/talents/${talentId}?lang=${odooLang}`,
            method: 'POST',
          });
          
          if (result.error) {
            console.error('[Talents API] Talent endpoint error:', result.error);
            return { error: result.error as any };
          }
          
          const response = result.data as any;
          
          if (!response || !response.success || !response.data) {
            console.warn('[Talents API] Talent endpoint returned no data');
            return { error: { status: 404, data: 'Talent not found' } as any };
          }
          
          // Transform the single talent response
          const talent = transformOdooTalentToTalent(response.data, lang);
          
          console.log('[Talents API] Found talent via ID endpoint:', talent.id);
          return { data: talent };
        } catch (error) {
          console.error('[Talents API] Get single talent error:', error);
          return { error: { status: 500, data: extractErrorMessage(error) } as any };
        }
      },
      providesTags: (_result, _error, { id }) => [{ type: 'Talent', id }],
    }),
  }),
});

export const {
  useGetTalentsQuery,
  useGetTalentQuery,
} = talentsApi;

